import React, { useState, useRef, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { FiSearch, FiChevronDown, FiX } from 'react-icons/fi';
import { colors, spacing, borderRadius, typography } from '../theme/colors';
import { focusNext } from '../utils/focusNext';

const ItemSelect = forwardRef(({ items, selectedItemId, onChange, disabled, stockBalances, placeholder = 'Select Item', allOption = null, onEnterLast, stockOnDemand = false, onRequestStock = null }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  // Items whose stock is currently being fetched (on-demand mode).
  const [stockLoading, setStockLoading] = useState({});
  const [search, setSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const isAllSelected = allOption ? selectedItemId === allOption.value : !selectedItemId;
  const selectedItem = useMemo(() =>
    items.find(i => i.id === selectedItemId) || null,
    [items, selectedItemId]
  );

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      i.itemName?.toLowerCase().includes(q) ||
      i.itemCode?.toLowerCase().includes(q) ||
      i.barcode?.toLowerCase().includes(q) ||
      i.description?.toLowerCase().includes(q)
    );
  }, [items, search]);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom,
        left: rect.left,
        width: Math.max(rect.width, 280)
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const handleScrollResize = () => updatePosition();
    window.addEventListener('scroll', handleScrollResize, true);
    window.addEventListener('resize', handleScrollResize);
    return () => {
      window.removeEventListener('scroll', handleScrollResize, true);
      window.removeEventListener('resize', handleScrollResize);
    };
  }, [isOpen, updatePosition]);

  const handleSelect = (item, { advanceFocus = false } = {}) => {
    onChange(item.id);
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(0);
    if (advanceFocus) {
      setTimeout(() => { if (triggerRef.current) focusNext(triggerRef.current); }, 30);
    } else {
      setTimeout(() => triggerRef.current?.focus(), 30);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(allOption ? allOption.value : '');
    setSearch('');
  };

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(0);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  useEffect(() => { setHighlightedIndex(0); }, [search]);

  useImperativeHandle(ref, () => ({
    focus: () => triggerRef.current?.focus(),
    open: () => handleOpen(),
  }), []);

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (!isOpen && selectedItem && e.key === 'Enter') {
      e.preventDefault();
      focusNext(triggerRef.current);
      return;
    }
    if (!isOpen && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      handleOpen();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, filteredItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = filteredItems[highlightedIndex];
      if (it) handleSelect(it, { advanceFocus: true });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setSearch('');
      setTimeout(() => triggerRef.current?.focus(), 30);
    } else if (e.key === 'Tab') {
      setIsOpen(false);
      setSearch('');
    }
  };

  const dropdown = isOpen ? ReactDOM.createPortal(
    <Dropdown
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 9999
      }}
    >
      {filteredItems.length === 0 ? (
        <NoResults>No items found</NoResults>
      ) : (
        filteredItems.map((item, idx) => {
          const qty = stockBalances?.[item.id];
          const isService = item.itemType === 'service';
          return (
            <DropdownItem
              key={item.id}
              onClick={() => handleSelect(item, { advanceFocus: true })}
              onMouseEnter={() => setHighlightedIndex(idx)}
              $selected={item.id === selectedItemId}
              $highlighted={idx === highlightedIndex}
            >
              {item.itemCode && <ItemCode>{item.itemCode}</ItemCode>}
              <ItemName>{item.itemName}</ItemName>
              {isService && <ServiceBadge>SERVICE</ServiceBadge>}
              {!isService && qty !== undefined && <StockBadge $negative={qty <= 0}>{Number.isInteger(qty) ? qty : Number(qty).toFixed(2)} {item.unitOfMeasure || 'Qty'}</StockBadge>}
              {/* Firebase cost: stock is NOT pre-loaded any more. Showing it for
                  every item meant downloading the company's entire stock history
                  on every form open. This chip fetches just this one item, only
                  when asked — same idea as "View Balance" on the customer picker. */}
              {!isService && qty === undefined && stockOnDemand && onRequestStock && (
                stockLoading[item.id]
                  ? <StockLink as="span" $busy>…</StockLink>
                  : (
                    <StockLink
                      title="Show stock for this item"
                      onClick={async (e) => {
                        e.stopPropagation();
                        setStockLoading(prev => ({ ...prev, [item.id]: true }));
                        try { await onRequestStock(item.id); }
                        finally { setStockLoading(prev => { const n = { ...prev }; delete n[item.id]; return n; }); }
                      }}
                    >stock?</StockLink>
                  )
              )}
              {!isService && qty === undefined && !stockOnDemand && item.unitOfMeasure && <ItemUnit>{item.unitOfMeasure}</ItemUnit>}
            </DropdownItem>
          );
        })
      )}
    </Dropdown>,
    document.body
  ) : null;

  return (
    <Wrapper ref={wrapperRef}>
      <SelectTrigger
        ref={triggerRef}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
        tabIndex={disabled ? -1 : 0}
        $disabled={disabled}
        $hasValue={!!selectedItem}
      >
        {isOpen ? (
          <SearchInputWrapper>
            <FiSearch size={12} />
            <SearchInput
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search item..."
              onClick={e => e.stopPropagation()}
            />
          </SearchInputWrapper>
        ) : (
          <TriggerText $hasValue={!!selectedItem}>
            {selectedItem ? `${selectedItem.itemCode ? selectedItem.itemCode + ' - ' : ''}${selectedItem.itemName}` : (allOption ? allOption.label : placeholder)}
          </TriggerText>
        )}
        <TriggerIcons>
          {selectedItem && !isAllSelected && !disabled && <ClearBtn onClick={handleClear}><FiX size={12} /></ClearBtn>}
          <FiChevronDown size={12} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
        </TriggerIcons>
      </SelectTrigger>
      {dropdown}
    </Wrapper>
  );
});

// ===== STYLED =====
const Wrapper = styled.div`position:relative;`;
const SelectTrigger = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:4px 8px;
  border:1px solid ${props => props.$hasValue ? colors.primary.main + '40' : colors.border.light};
  border-radius:3px;font-size:${typography.fontSize.sm};
  color:${colors.text.primary};background:${props => props.$disabled ? colors.neutral[50] : 'white'};
  cursor:${props => props.$disabled ? 'not-allowed' : 'pointer'};
  min-height:30px;transition:border-color 0.2s, box-shadow 0.2s;
  &:hover{border-color:${props => props.$disabled ? colors.border.light : colors.primary.main};}
  &:focus{outline:none;border-color:${colors.primary.main};box-shadow:0 0 0 2px ${colors.primary.main}30;}
`;
const TriggerText = styled.span`
  color:${props => props.$hasValue ? colors.text.primary : colors.text.tertiary};
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1;
`;
const TriggerIcons = styled.div`display:flex;align-items:center;gap:2px;color:${colors.text.secondary};flex-shrink:0;`;
const ClearBtn = styled.button`
  display:flex;align-items:center;justify-content:center;
  width:16px;height:16px;background:${colors.neutral[200]};border:none;
  border-radius:50%;cursor:pointer;color:${colors.text.secondary};
  &:hover{background:${colors.error.main};color:white;}
`;
const SearchInputWrapper = styled.div`display:flex;align-items:center;gap:4px;flex:1;color:${colors.text.secondary};`;
const SearchInput = styled.input`
  border:none;outline:none;background:none;flex:1;
  font-size:${typography.fontSize.sm};color:${colors.text.primary};min-width:0;
  &::placeholder{color:${colors.text.tertiary};}
`;
const Dropdown = styled.div`
  max-height:220px;overflow-y:auto;
  background:${colors.background.default};
  border:1px solid ${colors.border.light};
  border-radius:0 0 ${borderRadius.sm} ${borderRadius.sm};
  box-shadow:${colors.shadows?.md || '0 3px 6px rgba(0,0,0,0.15)'};
`;
const DropdownItem = styled.div`
  display:flex;align-items:center;gap:${spacing.sm};
  padding:6px 8px;cursor:pointer;
  background:${props => props.$highlighted ? colors.primary.main + '15' : props.$selected ? colors.primary.main + '10' : 'transparent'};
  border-left:${props => props.$selected ? `3px solid ${colors.primary.main}` : '3px solid transparent'};
  &:hover{background:${colors.neutral[50]};}
  &:not(:last-child){border-bottom:1px solid ${colors.neutral[100]};}
`;
const ItemCode = styled.span`
  font-size:11px;font-weight:${typography.fontWeight.semibold};
  color:${colors.primary.main};background:${colors.primary.main}10;
  padding:1px 5px;border-radius:3px;white-space:nowrap;
`;
const ItemName = styled.span`font-size:${typography.fontSize.sm};color:${colors.text.primary};flex:1;`;
const ItemUnit = styled.span`font-size:11px;color:${colors.text.tertiary};white-space:nowrap;`;
const StockBadge = styled.span`
  font-size:10px;font-weight:${typography.fontWeight.semibold};white-space:nowrap;
  padding:1px 6px;border-radius:3px;
  color:${props => props.$negative ? colors.error.main : colors.success.main};
  background:${props => props.$negative ? colors.error.main + '12' : colors.success.main + '12'};
`;
const StockLink = styled.button`
  font-size:10px;font-weight:${typography.fontWeight.semibold};white-space:nowrap;
  padding:1px 6px;border-radius:3px;border:1px dashed ${colors.text.tertiary}55;
  background:transparent;color:${colors.text.tertiary};cursor:pointer;line-height:1.6;
  opacity:${props => props.$busy ? 0.6 : 1};
  &:hover { color:${colors.primary.main};border-color:${colors.primary.main}66;background:${colors.primary.main}0d; }
`;
const ServiceBadge = styled.span`
  font-size:10px;font-weight:${typography.fontWeight.semibold};white-space:nowrap;
  padding:1px 6px;border-radius:3px;letter-spacing:0.5px;
  color:${colors.primary.main};background:${colors.primary.main}15;
`;
const NoResults = styled.div`padding:${spacing.md};text-align:center;color:${colors.text.tertiary};font-size:${typography.fontSize.sm};`;

export default ItemSelect;
